import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faXmark } from '@fortawesome/free-solid-svg-icons';
import { syncDailyMenuFromTemplate } from './dailyMenuApi';
import {
    fetchRestaurantFoodItems,
    loadGroupedOnlineMenuItems,
    isMenuItemActiveStatus,
    formatFoodItemDisplayName,
    getCatalogOptionsForTemplateSource,
    foodItemMatchesTemplateSourceCatalog,
    replaceFoodItemAgePricing,
} from './onlineFoodItems';
import defaultFoodImage from './assets/DefaultFood.jpg';
import MealTypesDropdown from './mealTypesDropdown';
import {
    addMenuTemplateItems,
    applyTemplateItemsFromResponse,
    buildBulkAddPayload,
    buildItemPayload,
    defaultPickerRowDraft,
    draftItemFromMenuRow,
    emptyItemForm,
    flattenMenuItems,
    getPickerRowDraft,
    getTemplateItemRowKey,
    itemFormFromRow,
    normalizeMenuTemplateItem,
    parseMealsCsv,
    templateItemUnifiedId,
    updateMenuTemplateItem,
} from './menuTemplateItemUtils';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, sortList, str } from './tableSortUtils';
import {
    AgePricingComboCell,
    AgePricingComboEditor,
    hasAgePricing,
} from './AgePricingDisplay';
import './css/AgePricingDisplay.css';
import './css/OrdersTablePagination.css';

function getTemplateItemSortValue(item, sortKey) {
    const row = normalizeMenuTemplateItem(item) || item || {};
    switch (sortKey) {
        case 'foodItem':
            return str(row.foodItemName ?? row.Food_Item);
        case 'category':
            return str(row.foodCategory ?? row.Food_Category);
        case 'foodType':
            return str(row.foodType ?? row.Food_Type ?? row.itemFoodType);
        case 'meal':
            return str(row.meal ?? row.Meal);
        case 'price':
            return Number.parseFloat(row.price ?? row.Price) || 0;
        case 'quantity':
            return str(row.quantity ?? row.Quantity);
        case 'stock':
            return Number.parseFloat(row.noOfItemsAvailable) || 0;
        case 'available':
            return row.isSelected === false ? 0 : 1;
        case 'notes':
            return str(row.notes ?? row.Notes);
        default:
            return '';
    }
}

function AvailabilityToggle({
    checked,
    onChange,
    disabled = false,
    readOnly = false,
    ariaLabel = 'Available',
}) {
    const stateClass = checked ? 'menu-availability-toggle--on' : 'menu-availability-toggle--off';
    const label = checked ? 'On' : 'Off';

    if (readOnly) {
        return (
            <span
                role="status"
                aria-label={ariaLabel}
                className={`menu-availability-toggle menu-availability-toggle--readonly ${stateClass}`}
            >
                {label}
            </span>
        );
    }

    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={ariaLabel}
            disabled={disabled}
            className={`menu-availability-toggle ${stateClass}`}
            onClick={() => onChange(!checked)}
        >
            {label}
        </button>
    );
}

async function runDailyMenuSyncForTemplate({
    templateId,
    createdById,
    restaurantId,
    templateSource,
}) {
    const id = Number(templateId);
    const { totalItemsAdded, results: syncResults } = await syncDailyMenuFromTemplate({
        templateId: id,
        createdById,
        restaurantId: Number(restaurantId) || undefined,
        templateSource,
    });
    const syncedDates = syncResults
        .filter((r) => r.ok && (r.itemsAdded ?? 0) > 0)
        .map((r) => r.menuDate);
    if (syncedDates.length > 0) {
        window.dispatchEvent(
            new CustomEvent('daily-menu-template-synced', {
                detail: { templateId: id, menuDates: syncedDates },
            })
        );
    }
    return totalItemsAdded;
}

const MenuTemplateItemsEditor = ({
    restaurantId,
    menuSource = 'MYEATS',
    items = [],
    onItemsChange,
    templateId = null,
    user = null,
    loading = false,
    disabled = false,
    showTitle = true,
    title = 'Template items',
    headerClassName = 'menu-template-items-editor-header',
    emptyMessage = 'No items in this template yet. Click Add from menu to select items.',
    syncDailyMenu = true,
}) => {
    const [menuFoodItems, setMenuFoodItems] = useState([]);
    const [showMenuPicker, setShowMenuPicker] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemForm, setItemForm] = useState(emptyItemForm);
    const [savingItem, setSavingItem] = useState(false);
    const [menuPickerSearch, setMenuPickerSearch] = useState('');
    const [selectedFoodIds, setSelectedFoodIds] = useState(() => new Set());
    const [pickerRowDrafts, setPickerRowDrafts] = useState({});
    const [menuLoadError, setMenuLoadError] = useState('');
    const [jreatsMenuMeta, setJreatsMenuMeta] = useState(null);
    const [itemsPage, setItemsPage] = useState(1);
    const [itemsPageSize, setItemsPageSize] = useState(10);
    const [itemsSort, setItemsSort] = useState(null);

    const menuSourceFilter = useMemo(
        () => String(menuSource ?? 'MYEATS').trim().toUpperCase(),
        [menuSource]
    );

    const isPersisted = templateId != null && String(templateId).trim() !== '';

    const loadMenuFoodItems = useCallback(async () => {
        if (!restaurantId) return;
        setMenuLoadError('');
        setJreatsMenuMeta(null);
        try {
            if (menuSourceFilter === 'JREATS') {
                const { items: grouped, meta } = await loadGroupedOnlineMenuItems(
                    restaurantId,
                    'JREATS'
                );
                setJreatsMenuMeta(meta);
                setMenuFoodItems(
                    flattenMenuItems(grouped).filter((item) =>
                        isMenuItemActiveStatus(item.Status ?? item.status)
                    )
                );
                return;
            }

            const grouped = await fetchRestaurantFoodItems(
                restaurantId,
                getCatalogOptionsForTemplateSource(menuSourceFilter)
            );
            setMenuFoodItems(
                flattenMenuItems(grouped).filter(
                    (item) =>
                        isMenuItemActiveStatus(item.Status ?? item.status) &&
                        foodItemMatchesTemplateSourceCatalog(item, menuSourceFilter)
                )
            );
        } catch (err) {
            console.error('Error loading menu food items:', err?.response?.data || err?.message);
            const msg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load menu items.';
            setMenuLoadError(msg);
            setMenuFoodItems([]);
            setJreatsMenuMeta(null);
        }
    }, [restaurantId, menuSourceFilter]);

    useEffect(() => {
        loadMenuFoodItems();
    }, [loadMenuFoodItems]);

    const sortedItems = useMemo(
        () => sortList(items, itemsSort, getTemplateItemSortValue),
        [items, itemsSort]
    );

    const { handleSortAsc: handleItemsSortAsc, handleSortDesc: handleItemsSortDesc } =
        buildSortHandlers(setItemsSort, () => setItemsPage(1));

    const totalItemPages = Math.max(1, Math.ceil(sortedItems.length / itemsPageSize));

    const paginatedItems = useMemo(() => {
        const start = (itemsPage - 1) * itemsPageSize;
        return sortedItems.slice(start, start + itemsPageSize);
    }, [sortedItems, itemsPage, itemsPageSize]);

    useEffect(() => {
        if (itemsPage > totalItemPages) {
            setItemsPage(totalItemPages);
        }
    }, [itemsPage, totalItemPages, itemsPageSize]);

    const handleItemsPageSizeChange = (nextSize) => {
        setItemsPageSize(nextSize);
        setItemsPage(1);
    };

    const templateFoodIdSet = useMemo(
        () => new Set(items.map((i) => String(templateItemUnifiedId(i) ?? ''))),
        [items]
    );

    const pickerMenuItems = useMemo(() => {
        const q = menuPickerSearch.trim().toLowerCase();
        return menuFoodItems.filter((m) => {
            const id = String(m.Food_Item_Id ?? '');
            if (templateFoodIdSet.has(id)) return false;
            if (!q) return true;
            return [
                m.Food_Item,
                m.Food_Category,
                m.foodCategory,
                m.Food_Type,
                m.Price,
                m.itemFoodType,
            ].some((v) => String(v ?? '').toLowerCase().includes(q));
        });
    }, [menuFoodItems, menuPickerSearch, templateFoodIdSet]);

    const selectedCount = selectedFoodIds.size;

    const allPickerSelected =
        pickerMenuItems.length > 0 &&
        pickerMenuItems.every((m) => selectedFoodIds.has(String(m.Food_Item_Id)));

    const toggleFoodSelection = (foodItemId) => {
        const key = String(foodItemId);
        setSelectedFoodIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSelectAllPicker = () => {
        if (allPickerSelected) {
            setSelectedFoodIds(new Set());
            return;
        }
        setSelectedFoodIds(new Set(pickerMenuItems.map((m) => String(m.Food_Item_Id))));
    };

    const updatePickerRowDraft = (foodId, patch) => {
        setPickerRowDrafts((prev) => ({
            ...prev,
            [foodId]: { ...getPickerRowDraft(prev, foodId), ...patch },
        }));
    };

    const openMenuPicker = () => {
        if (disabled) return;
        const drafts = {};
        menuFoodItems.forEach((m) => {
            const id = String(m.Food_Item_Id ?? '');
            if (id && !templateFoodIdSet.has(id)) {
                drafts[id] = defaultPickerRowDraft(m);
            }
        });
        setPickerRowDrafts(drafts);
        setSelectedFoodIds(new Set());
        setMenuPickerSearch('');
        setShowMenuPicker(true);
    };

    const closeMenuPicker = () => {
        if (savingItem) return;
        setShowMenuPicker(false);
        setSelectedFoodIds(new Set());
        setPickerRowDrafts({});
        setMenuPickerSearch('');
    };

    const openEditItemModal = (item) => {
        if (disabled) return;
        const normalized = normalizeMenuTemplateItem(item) || item;
        setEditingItem(normalized);
        setItemForm(itemFormFromRow(normalized));
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        if (savingItem) return;
        setShowEditModal(false);
        setEditingItem(null);
        setItemForm(emptyItemForm());
    };

    const handleBulkAdd = async () => {
        if (selectedCount === 0) {
            alert('Select at least one menu item.');
            return;
        }
        const actorId = Number(user?.userId) || Number(restaurantId) || 1;
        const payloads = [];
        const draftRows = [];
        for (const foodId of selectedFoodIds) {
            const row = menuFoodItems.find((m) => String(m.Food_Item_Id) === foodId);
            if (!row) continue;
            const draft = getPickerRowDraft(pickerRowDrafts, foodId, row);
            if (parseMealsCsv(draft.meal).length === 0) {
                alert(`Select at least one meal type for "${row.Food_Item}".`);
                return;
            }
            if (!String(draft.price ?? '').trim() || Number.isNaN(Number(draft.price))) {
                alert(`Enter price for "${row.Food_Item}".`);
                return;
            }
            payloads.push(buildBulkAddPayload(row, draft, actorId));
            draftRows.push(draftItemFromMenuRow(row, draft));
        }
        if (payloads.length === 0) {
            alert('No valid items selected.');
            return;
        }

        setSavingItem(true);
        try {
            if (isPersisted) {
                const response = await addMenuTemplateItems(templateId, payloads);
                let message = response.data?.message || 'Items added.';
                const hasSelectedAdd = payloads.some((p) => p.isSelected === 1);
                if (syncDailyMenu && hasSelectedAdd) {
                    const totalItemsAdded = await runDailyMenuSyncForTemplate({
                        templateId,
                        createdById: actorId,
                        restaurantId,
                        templateSource: menuSourceFilter,
                    });
                    if (totalItemsAdded > 0) {
                        message += ` ${totalItemsAdded} new item(s) added to daily menu.`;
                    }
                }
                alert(message);
                const nextItems = applyTemplateItemsFromResponse(response.data);
                if (nextItems) {
                    onItemsChange(nextItems);
                } else {
                    onItemsChange([...items, ...draftRows]);
                }
            } else {
                onItemsChange([...items, ...draftRows]);
            }
            closeMenuPicker();
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to add template items.';
            alert(`Error: ${msg}`);
        } finally {
            setSavingItem(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        const foodItemId = Number(itemForm.foodItemId);
        if (!foodItemId) {
            alert('Item is required.');
            return;
        }
        if (parseMealsCsv(itemForm.meal).length === 0) {
            alert('Select at least one meal type.');
            return;
        }
        if (String(itemForm.price ?? '').trim() === '' || Number.isNaN(Number(itemForm.price))) {
            alert('Enter a valid price.');
            return;
        }
        if (
            menuSourceFilter === 'JREATS' &&
            Array.isArray(itemForm.agePricing) &&
            itemForm.agePricing.some(
                (tier) =>
                    String(tier.price ?? '').trim() === '' || Number.isNaN(Number(tier.price))
            )
        ) {
            alert('Enter a valid price for each age group.');
            return;
        }
        const userId = user?.userId;
        const actorId = Number(userId) || Number(restaurantId) || 1;
        const wasSelected = Boolean(editingItem?.isSelected);
        const nowSelected = Boolean(itemForm.isSelected);
        const rowKey = getTemplateItemRowKey(editingItem);

        setSavingItem(true);
        try {
            if (isPersisted && editingItem?.tmpItemId) {
                const payload = buildItemPayload(itemForm, userId, restaurantId, true);
                const response = await updateMenuTemplateItem(editingItem.tmpItemId, payload);
                let message = response.data?.message || 'Template line updated.';
                let nextAgePricing = itemForm.agePricing;
                if (
                    menuSourceFilter === 'JREATS' &&
                    foodItemId > 0 &&
                    Array.isArray(itemForm.agePricing) &&
                    itemForm.agePricing.length > 0
                ) {
                    const ageRes = await replaceFoodItemAgePricing(
                        foodItemId,
                        itemForm.agePricing,
                        actorId
                    );
                    nextAgePricing = ageRes.data?.agePricing ?? itemForm.agePricing;
                }
                if (syncDailyMenu && nowSelected && !wasSelected) {
                    const totalItemsAdded = await runDailyMenuSyncForTemplate({
                        templateId,
                        createdById: actorId,
                        restaurantId,
                        templateSource: menuSourceFilter,
                    });
                    if (totalItemsAdded > 0) {
                        message += ` ${totalItemsAdded} new item(s) added to daily menu.`;
                    }
                }
                alert(message);
                const updated = normalizeMenuTemplateItem(response.data?.item);
                if (updated?.tmpItemId) {
                    onItemsChange(
                        items.map((row) =>
                            String(getTemplateItemRowKey(row)) === String(rowKey)
                                ? {
                                      ...row,
                                      ...updated,
                                      agePricing: nextAgePricing,
                                      price: itemForm.price,
                                  }
                                : row
                        )
                    );
                }
            } else {
                const updated = normalizeMenuTemplateItem({
                    ...editingItem,
                    foodItemId,
                    meal: itemForm.meal,
                    price: itemForm.price,
                    noOfItemsAvailable: itemForm.noOfItemsAvailable,
                    notes: itemForm.notes,
                    isSelected: itemForm.isSelected,
                    agePricing: itemForm.agePricing,
                });
                onItemsChange(
                    items.map((row) =>
                        String(getTemplateItemRowKey(row)) === String(rowKey) ? updated : row
                    )
                );
            }
            closeEditModal();
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Failed to update template item.';
            alert(`Error: ${msg}`);
        } finally {
            setSavingItem(false);
        }
    };

    return (
        <>
            <div className="menu-template-items-editor">
                <div className={headerClassName}>
                    {showTitle ? (
                        <h3 className="restaurant-detail-section-title menu-template-items-editor-title">
                            {title}
                        </h3>
                    ) : (
                        <span className="menu-template-items-editor-label">{title}</span>
                    )}
                    <div className="restaurant-detail-actions menu-template-items-editor-actions">
                        <button
                            type="button"
                            className="add-button"
                            onClick={openMenuPicker}
                            disabled={disabled || loading}
                            aria-label="Add from menu"
                        >
                            <span className="menu-template-add-btn-label-full">Add from menu</span>
                            <span className="menu-template-add-btn-label-short" aria-hidden="true">
                                Add
                            </span>
                        </button>
                    </div>
                </div>
                {menuSourceFilter === 'JREATS' && jreatsMenuMeta?.state ? (
                    <p className="menu-template-jreats-state-hint">
                        Junior Eats statewide menu for{' '}
                        <strong>{jreatsMenuMeta.state}</strong>
                        {jreatsMenuMeta.restaurantState &&
                        String(jreatsMenuMeta.restaurantState).toLowerCase() !==
                            String(jreatsMenuMeta.state).toLowerCase()
                            ? ` (restaurant state: ${jreatsMenuMeta.restaurantState})`
                            : ''}
                    </p>
                ) : null}
                {menuLoadError ? (
                    <p className="menu-template-items-load-error">{menuLoadError}</p>
                ) : null}
                <div className="menu-table-wrapper menu-template-items-table-wrapper">
                    <table className="menu-table">
                        <thead>
                            <tr>
                                <OrdersSortableTh label="Food Item" sortKey="foodItem" activeSortKey={itemsSort?.key} sortDirection={itemsSort?.direction} onSortAsc={handleItemsSortAsc} onSortDesc={handleItemsSortDesc} />
                                <OrdersSortableTh label="Food Category" sortKey="category" activeSortKey={itemsSort?.key} sortDirection={itemsSort?.direction} onSortAsc={handleItemsSortAsc} onSortDesc={handleItemsSortDesc} />
                                <OrdersSortableTh label="Food Type" sortKey="foodType" activeSortKey={itemsSort?.key} sortDirection={itemsSort?.direction} onSortAsc={handleItemsSortAsc} onSortDesc={handleItemsSortDesc} />
                                <OrdersSortableTh label="Meal" sortKey="meal" activeSortKey={itemsSort?.key} sortDirection={itemsSort?.direction} onSortAsc={handleItemsSortAsc} onSortDesc={handleItemsSortDesc} />
                                <OrdersSortableTh
                                    label={
                                        menuSourceFilter === 'JREATS'
                                            ? 'Price & Quantity'
                                            : 'Price ₹'
                                    }
                                    sortKey="price"
                                    activeSortKey={itemsSort?.key}
                                    sortDirection={itemsSort?.direction}
                                    onSortAsc={handleItemsSortAsc}
                                    onSortDesc={handleItemsSortDesc}
                                />
                                {menuSourceFilter === 'JREATS' ? null : (
                                    <OrdersSortableTh
                                        label="Quantity"
                                        sortKey="quantity"
                                        activeSortKey={itemsSort?.key}
                                        sortDirection={itemsSort?.direction}
                                        onSortAsc={handleItemsSortAsc}
                                        onSortDesc={handleItemsSortDesc}
                                    />
                                )}
                                <OrdersSortableTh label="Stock" sortKey="stock" activeSortKey={itemsSort?.key} sortDirection={itemsSort?.direction} onSortAsc={handleItemsSortAsc} onSortDesc={handleItemsSortDesc} />
                                <OrdersSortableTh label="Available" sortKey="available" activeSortKey={itemsSort?.key} sortDirection={itemsSort?.direction} onSortAsc={handleItemsSortAsc} onSortDesc={handleItemsSortDesc} />
                                <OrdersSortableTh label="Notes" sortKey="notes" activeSortKey={itemsSort?.key} sortDirection={itemsSort?.direction} onSortAsc={handleItemsSortAsc} onSortDesc={handleItemsSortDesc} />
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={menuSourceFilter === 'JREATS' ? 9 : 10}
                                        className="menu-table-empty-cell menu-empty-filtered"
                                    >
                                        Loading items…
                                    </td>
                                </tr>
                            ) : items.length > 0 ? (
                                paginatedItems.map((item) => {
                                    const row = normalizeMenuTemplateItem(item) || item;
                                    return (
                                        <tr key={getTemplateItemRowKey(row)}>
                                            <td className="menu-template-picker-food-cell">
                                                <div className="menu-food-cell-inner">
                                                <img
                                                    src={
                                                        row.foodItemImageUrl || defaultFoodImage
                                                    }
                                                    alt=""
                                                    className="menu-template-item-thumb"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = defaultFoodImage;
                                                    }}
                                                />
                                                <span>{formatFoodItemDisplayName(row.foodItemName) || '—'}</span>
                                                </div>
                                            </td>
                                            <td className="menu-table-cell-muted">
                                                {row.foodCategory ?? '—'}
                                            </td>
                                            <td>{row.foodType ?? '—'}</td>
                                            <td>{row.meal ?? '—'}</td>
                                            <td>
                                                {menuSourceFilter === 'JREATS' ? (
                                                    <AgePricingComboCell
                                                        item={row}
                                                        fallbackPrice={row.price ?? row.basePrice}
                                                        fallbackQuantity={row.quantity}
                                                    />
                                                ) : (
                                                    row.price ?? row.basePrice ?? '—'
                                                )}
                                            </td>
                                            {menuSourceFilter === 'JREATS' ? null : (
                                                <td>
                                                    {row.quantity
                                                        ? String(row.quantity).trim()
                                                        : '—'}
                                                </td>
                                            )}
                                            <td>{row.noOfItemsAvailable ?? '—'}</td>
                                            <td className="menu-template-availability-cell">
                                                <AvailabilityToggle
                                                    checked={Boolean(row.isSelected)}
                                                    readOnly
                                                    ariaLabel={`Available for ${row.foodItemName}`}
                                                />
                                            </td>
                                            <td>{row.notes ?? '—'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="action-button"
                                                    title="Edit item"
                                                    onClick={() => openEditItemModal(row)}
                                                    disabled={disabled}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={menuSourceFilter === 'JREATS' ? 9 : 10}
                                        className="menu-table-empty-cell menu-empty-filtered"
                                    >
                                        {emptyMessage}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {!loading && items.length > 0 ? (
                    <OrdersTablePagination
                        currentPage={itemsPage}
                        totalPages={totalItemPages}
                        totalItems={sortedItems.length}
                        pageSize={itemsPageSize}
                        onPageChange={setItemsPage}
                        onPageSizeChange={handleItemsPageSizeChange}
                        itemLabel="items"
                        ariaLabel="Menu template items pagination"
                    />
                ) : null}
            </div>

            {showMenuPicker ? (
                <div
                    className="inventory-modal-overlay"
                    onClick={closeMenuPicker}
                    role="presentation"
                >
                    <div
                        className="inventory-modal menu-template-modal menu-template-picker-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="menu-template-picker-title"
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4 id="menu-template-picker-title">Add from menu</h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={closeMenuPicker}
                                aria-label="Close"
                                disabled={savingItem}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <div className="menu-template-picker-panel">
                            <div className="menu-template-picker-toolbar">
                                <input
                                    type="text"
                                    className="add-menu-form-field menu-template-picker-search"
                                    placeholder="Search menu…"
                                    value={menuPickerSearch}
                                    onChange={(e) => setMenuPickerSearch(e.target.value)}
                                />
                                <div className="menu-template-picker-toolbar-actions">
                                    <button
                                        type="button"
                                        className="close-button menu-template-picker-toolbar-btn"
                                        onClick={toggleSelectAllPicker}
                                        disabled={pickerMenuItems.length === 0}
                                    >
                                        {allPickerSelected ? 'Deselect all' : 'Select all'}
                                    </button>
                                    <button
                                        type="button"
                                        className="add-button menu-template-picker-toolbar-btn"
                                        onClick={handleBulkAdd}
                                        disabled={savingItem || selectedCount === 0}
                                    >
                                        {savingItem
                                            ? 'Adding…'
                                            : `Add selected (${selectedCount})`}
                                    </button>
                                </div>
                            </div>
                            {pickerMenuItems.length > 0 ? (
                                <div className="menu-table-wrapper menu-template-picker-table-wrap">
                                    <table className="menu-table">
                                        <thead>
                                            <tr>
                                                <th
                                                    className="menu-template-picker-check-col"
                                                    aria-hidden="true"
                                                />
                                                <th>Food Item</th>
                                                <th>Food Category</th>
                                                <th>Food Type</th>
                                                <th>
                                                    {menuSourceFilter === 'JREATS'
                                                        ? 'Price & Quantity'
                                                        : 'Price ₹'}
                                                </th>
                                                <th>Meal</th>
                                                {menuSourceFilter === 'JREATS' ? null : (
                                                    <th>Quantity</th>
                                                )}
                                                <th>Stock</th>
                                                <th>Notes</th>
                                                <th>Available</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pickerMenuItems.map((m) => {
                                                const fid = String(m.Food_Item_Id);
                                                const checked = selectedFoodIds.has(fid);
                                                const draft = getPickerRowDraft(
                                                    pickerRowDrafts,
                                                    fid,
                                                    m
                                                );
                                                return (
                                                    <tr
                                                        key={fid}
                                                        className={
                                                            checked
                                                                ? 'menu-template-picker-row--selected'
                                                                : ''
                                                        }
                                                    >
                                                        <td className="menu-template-picker-check-col">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() =>
                                                                    toggleFoodSelection(fid)
                                                                }
                                                                aria-label={`Select ${m.Food_Item}`}
                                                            />
                                                        </td>
                                                        <td className="menu-template-picker-food-cell">
                                                            <div className="menu-food-cell-inner">
                                                            <img
                                                                src={
                                                                    m.imageUrl || defaultFoodImage
                                                                }
                                                                alt=""
                                                                className="menu-template-item-thumb"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = defaultFoodImage;
                                                                }}
                                                            />
                                                            <span>{formatFoodItemDisplayName(m.Food_Item)}</span>
                                                            </div>
                                                        </td>
                                                        <td className="menu-table-cell-muted">
                                                            {m.Food_Category ??
                                                                m.foodCategory ??
                                                                '—'}
                                                        </td>
                                                        <td>
                                                            {m.itemFoodType || m.Food_Type || '—'}
                                                        </td>
                                                        <td className="menu-template-picker-field-cell">
                                                            {menuSourceFilter === 'JREATS' &&
                                                            hasAgePricing(m) ? (
                                                                <AgePricingComboCell
                                                                    item={m}
                                                                    fallbackPrice={m.Price}
                                                                    fallbackQuantity={
                                                                        m.Quantity ?? m.quantity
                                                                    }
                                                                />
                                                            ) : (
                                                                <input
                                                                    type="number"
                                                                    className="menu-template-picker-cell-input"
                                                                    value={draft.price}
                                                                    onChange={(e) =>
                                                                        updatePickerRowDraft(fid, {
                                                                            price: e.target.value,
                                                                        })
                                                                    }
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="menu-template-picker-field-cell menu-template-picker-meal-cell">
                                                            <div className="daily-menu menu-template-meal-dropdown">
                                                                <MealTypesDropdown
                                                                    value={draft.meal}
                                                                    onCsvChange={(meal) =>
                                                                        updatePickerRowDraft(fid, {
                                                                            meal,
                                                                        })
                                                                    }
                                                                    id={`picker-${fid}`}
                                                                />
                                                            </div>
                                                        </td>
                                                        {menuSourceFilter === 'JREATS' ? null : (
                                                            <td className="menu-table-cell-muted">
                                                                {m.Quantity ?? m.quantity ?? '—'}
                                                            </td>
                                                        )}
                                                        <td className="menu-template-picker-field-cell">
                                                            <input
                                                                type="number"
                                                                className="menu-template-picker-cell-input"
                                                                value={draft.noOfItemsAvailable}
                                                                onChange={(e) =>
                                                                    updatePickerRowDraft(fid, {
                                                                        noOfItemsAvailable:
                                                                            e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </td>
                                                        <td className="menu-template-picker-field-cell">
                                                            <input
                                                                type="text"
                                                                className="menu-template-picker-cell-input"
                                                                value={draft.notes}
                                                                onChange={(e) =>
                                                                    updatePickerRowDraft(fid, {
                                                                        notes: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </td>
                                                        <td className="menu-template-availability-cell">
                                                            <AvailabilityToggle
                                                                checked={draft.isSelected}
                                                                onChange={(next) =>
                                                                    updatePickerRowDraft(fid, {
                                                                        isSelected: next,
                                                                    })
                                                                }
                                                                ariaLabel={`Available for ${m.Food_Item}`}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="menu-empty-filtered">
                                    {menuLoadError
                                        ? menuLoadError
                                        : menuFoodItems.length === 0
                                          ? menuSourceFilter === 'JREATS'
                                              ? 'No Junior Eats menu items found for this restaurant state.'
                                              : 'No active menu items available for this restaurant.'
                                          : 'All active menu items are already in this template, or none match your search.'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}

            {showEditModal && editingItem ? (
                <div
                    className="inventory-modal-overlay"
                    onClick={closeEditModal}
                    role="presentation"
                >
                    <div
                        className="inventory-modal menu-template-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="inventory-modal-header menu-template-modal-header">
                            <h4>Edit template item</h4>
                            <button
                                type="button"
                                className="menu-template-modal-close"
                                onClick={closeEditModal}
                                aria-label="Close"
                                disabled={savingItem}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <form
                            className="inventory-form inventory-form--supplier-modal menu-template-edit-form"
                            onSubmit={handleEditSubmit}
                        >
                            <div className="inventory-form-group menu-template-edit-full-row">
                                <label>Food item</label>
                                <input
                                    type="text"
                                    className="add-menu-form-field inventory-readonly-field"
                                    readOnly
                                    disabled
                                    value={editingItem.foodItemName ?? ''}
                                />
                            </div>
                            <div className="menu-template-edit-meal-avail-row">
                                <div className="inventory-form-group menu-template-edit-meal-group daily-menu">
                                    <label className="menu-form-label--required">Meal types</label>
                                    <MealTypesDropdown
                                        value={itemForm.meal}
                                        onCsvChange={(meal) =>
                                            setItemForm((p) => ({ ...p, meal }))
                                        }
                                        id={`edit-${editingItem.tmpItemId ?? editingItem.localKey}`}
                                    />
                                </div>
                                <div className="inventory-form-group menu-template-edit-avail-group">
                                    <label>Available</label>
                                    <AvailabilityToggle
                                        checked={itemForm.isSelected}
                                        onChange={(next) =>
                                            setItemForm((p) => ({ ...p, isSelected: next }))
                                        }
                                        disabled={savingItem}
                                        ariaLabel="Available"
                                    />
                                </div>
                            </div>
                            <div className="menu-template-edit-price-qty-row">
                                {menuSourceFilter === 'JREATS' ? (
                                    <div className="inventory-form-group menu-template-edit-full-row">
                                        <label className="menu-form-label--required">Price</label>
                                        <AgePricingComboEditor
                                            item={editingItem}
                                            basePrice={itemForm.price}
                                            baseQuantity={editingItem?.quantity}
                                            agePricing={itemForm.agePricing}
                                            disabled={savingItem}
                                            onChange={({ basePrice, agePricing }) =>
                                                setItemForm((p) => ({
                                                    ...p,
                                                    price: basePrice,
                                                    agePricing,
                                                }))
                                            }
                                        />
                                    </div>
                                ) : (
                                    <div className="inventory-form-group">
                                        <label htmlFor="template-item-price" className="menu-form-label--required">
                                            Price
                                        </label>
                                        <input
                                            id="template-item-price"
                                            type="number"
                                            className="add-menu-form-field"
                                            value={itemForm.price}
                                            onChange={(e) =>
                                                setItemForm((p) => ({ ...p, price: e.target.value }))
                                            }
                                            required
                                        />
                                    </div>
                                )}
                                {menuSourceFilter === 'JREATS' ? null : (
                                    <div className="inventory-form-group">
                                        <label htmlFor="template-item-menu-qty">Menu quantity</label>
                                        <input
                                            id="template-item-menu-qty"
                                            type="text"
                                            className="add-menu-form-field"
                                            value={
                                                editingItem?.quantity
                                                    ? String(editingItem.quantity).trim()
                                                    : '—'
                                            }
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                )}
                                <div className="inventory-form-group">
                                    <label htmlFor="template-item-qty">Stock</label>
                                    <input
                                        id="template-item-qty"
                                        type="number"
                                        className="add-menu-form-field"
                                        value={itemForm.noOfItemsAvailable}
                                        onChange={(e) =>
                                            setItemForm((p) => ({
                                                ...p,
                                                noOfItemsAvailable: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                            <div className="inventory-form-group menu-template-edit-full-row">
                                <label htmlFor="template-item-notes">Notes</label>
                                <input
                                    id="template-item-notes"
                                    type="text"
                                    className="add-menu-form-field"
                                    value={itemForm.notes}
                                    onChange={(e) =>
                                        setItemForm((p) => ({ ...p, notes: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="inventory-form-actions menu-template-modal-actions menu-template-edit-modal-actions">
                                <button
                                    type="submit"
                                    className="add-button menu-template-edit-submit-btn"
                                    disabled={savingItem}
                                >
                                    {savingItem ? 'Saving…' : 'Update Item'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default MenuTemplateItemsEditor;
